<script lang="ts">
    import cn from 'classnames';

    import { Avatar, type AvatarProps } from '@uikit/avatar';
    import type { DataAttrs } from '@uikit/types';
    import { SvgIcon, SvgIconTypes } from '@uikit/svg-icon';
    import { Font, FontTypes } from '@uikit/font';

    import { type Action, setActionFunc } from '@uikit/actions';

    import { type IconData, type ClickHandlerFn, BorderRadius, TagName, Color, Size, Target } from './types';
    import s from './tag.module.css';

    export let label: string;
    export let tagName: NullableType<TagName> = TagName.Div;
    export let color: NullableType<Color> = Color.Primary;
    export let size: NullableType<Size> = Size.Medium;
    export let borderRadius: NullableType<BorderRadius> = BorderRadius.Small;
    export let classes: NullableString = undefined;
    export let labelClasses: NullableString = undefined;
    export let iconLeft: NullableType<IconData> = undefined;
    export let iconRight: NullableType<IconData> = undefined;
    export let strikethrough: boolean = false;
    export let noPaddings: boolean = false;
    export let bold: boolean = false;
    export let href: NullableString = undefined;
    export let attrs: NullableType<Partial<DataAttrs>> = undefined;
    export let target: NullableType<Target> = Target.self;
    export let disabled: boolean = false;
    export let avatar: NullableType<AvatarProps> = undefined;
    export let clickHandler: NullableType<ClickHandlerFn> = undefined;
    export let iconLeftClickHandler: NullableType<ClickHandlerFn> = undefined;
    export let iconRightClickHandler: NullableType<ClickHandlerFn> = undefined;
    export let action: Action | undefined = undefined;

    $: labelFontType = size === Size.Small ? FontTypes.Type.caption : FontTypes.Type.body;
    $: labelColor = color === Color.Secondary ? FontTypes.Color.Invert : null;
    $: tagName = href ? TagName.A : tagName;
    $: target = href ? target : undefined;
    $: ({ actionFunc, actionParams } = setActionFunc(action));
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<svelte:element
    this={tagName}
    class={cn(
        s.container,
        s[`${color}`],
        s[`${size}`],
        s[`border-radius-${borderRadius}`],
        {
            [s.strikethrough]: strikethrough,
            [s.pointer]: clickHandler,
            [s.noPaddings]: noPaddings,
            [s.disabled]: disabled,
        },
        classes,
    )}
    {href}
    {target}
    data-testid="tag-container"
    {...attrs}
    on:click={clickHandler}
    use:actionFunc={actionParams}
>
    {#if avatar}
        <Avatar {...avatar} classes={s.avatar} shapeContainerClasses={s.avatarShape} />
    {/if}
    <div class={s.content}>
        <slot name="prefix" />
        {#if iconLeft}
            <SvgIcon
                icon={iconLeft}
                color={SvgIconTypes.Color.unset}
                attrs={{ 'data-testid': 'tag-icon-left' }}
                classes={cn(s.iconLeft, { [s.pointer]: iconLeftClickHandler })}
                on:click={iconLeftClickHandler}
            />
        {/if}
        <Font
            tagName={FontTypes.TagName.span}
            type={labelFontType}
            className={cn(s.label, labelClasses)}
            color={labelColor}
            isBold={bold}
        >
            <slot name="label">{label}</slot>
        </Font>
        {#if iconRight}
            <SvgIcon
                icon={iconRight}
                color={SvgIconTypes.Color.unset}
                attrs={{ 'data-testid': 'tag-icon-right' }}
                classes={cn(s.iconRight, { [s.pointer]: iconRightClickHandler })}
                on:click={iconRightClickHandler}
            />
        {/if}
        <slot name="suffix" />
    </div>
</svelte:element>
